import { formatVariableValue } from "@velizhanin-dev/bot-scenario"
import { ChevronRight } from "lucide-react"
import {
	type AudienceMember,
	type AudienceVariable,
	formatRelative,
	MemberAvatar,
	memberName,
	rubles,
	StateLabel,
} from "@/entities/audience"
import { cn } from "@/shared/lib/utils"
import { Button } from "@/shared/ui/button"
import { Checkbox } from "@/shared/ui/checkbox"
import { StatusBadge } from "@/shared/ui/status-badge"

export function MemberRow({
	member,
	variables,
	onOpen,
	selected,
	onToggle,
}: {
	member: AudienceMember
	variables: AudienceVariable[]
	onOpen: () => void
	/** Галочка слева: отметить человека для действий над выбранными. */
	selected?: boolean
	onToggle?: () => void
}) {
	const preview = variables
		.filter((variable) => variable.type !== "FILE")
		.map((variable) => [variable.name, member.vars[variable.name]] as const)
		.filter(([, value]) => value !== undefined && value !== "")
		.slice(0, 2)

	return (
		<li className={cn("flex items-stretch", selected && "bg-primary/5")}>
			{onToggle ? (
				<label className="flex shrink-0 cursor-pointer items-center pl-4">
					<Checkbox
						className="border-muted-foreground/60"
						checked={selected}
						onCheckedChange={onToggle}
						aria-label={`Выбрать: ${memberName(member)}`}
					/>
				</label>
			) : null}
			<Button
				type="button"
				variant="ghost"
				onClick={onOpen}
				className="h-auto min-w-0 flex-1 justify-start gap-3 rounded-none px-4 py-3 text-left hover:bg-accent/50"
			>
				<MemberAvatar member={member} />
				<div className="min-w-0 flex-1">
					<div className="flex min-w-0 items-baseline gap-2">
						<span className="truncate font-medium">{memberName(member)}</span>
						{member.username && (member.firstName || member.lastName) ? (
							<span className="truncate text-xs text-muted-foreground">@{member.username}</span>
						) : null}
					</div>
					<div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground">
						<StateLabel state={member.state} />
						<span aria-hidden>·</span>
						<span>{formatRelative(member.lastEventAt)}</span>
						{member.entryLink ? (
							<>
								<span aria-hidden>·</span>
								<span className="font-mono">{member.entryLink}</span>
							</>
						) : null}
					</div>
					{preview.length > 0 ? (
						<div className="mt-1.5 flex flex-wrap gap-1">
							{preview.map(([name, value]) => (
								<span
									key={name}
									className="max-w-full truncate rounded-md bg-muted/60 px-1.5 py-0.5 text-[11px] text-muted-foreground"
								>
									<span className="font-mono">{name}</span>: {formatVariableValue(value as never)}
								</span>
							))}
						</div>
					) : null}
				</div>
				{member.paidAmount > 0 ? (
					<StatusBadge tone="success" className="tabular-nums">
						{rubles(member.paidAmount)}
					</StatusBadge>
				) : null}
				<ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
			</Button>
		</li>
	)
}
